import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-leave-request-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-6xl mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/operations/leave-requests" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i> Leave Requests
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Leave Request Details</h1>
          </div>
        </header>

        @if (isLoading()) {
          <div class="rounded-xl border border-slate-200 bg-white p-16 text-center text-sm text-slate-400">Loading leave request...</div>
        } @else if (request()) {
          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
            <main class="space-y-5">
              <section class="rounded-xl border border-[#E7EEF5] bg-white shadow-sm overflow-hidden">
                <div class="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
                  <div>
                    <p class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Employee</p>
                    <h2 class="mt-1 text-lg font-semibold text-slate-950">{{ request().employee_name }}</h2>
                    <p class="text-xs text-slate-500">{{ request().employee_email }} · {{ request().employee_phone || 'No phone number' }}</p>
                  </div>
                  <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(request().status)">{{ request().status }}</span>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-100">
                  <div class="bg-white p-4"><p class="label">Leave type</p><p class="value">{{ request().leave_type }}</p></div>
                  <div class="bg-white p-4"><p class="label">Leave dates</p><p class="value">{{ request().start_date | date:'dd MMM yyyy' }} – {{ request().end_date | date:'dd MMM yyyy' }}</p></div>
                  <div class="bg-white p-4"><p class="label">Duration</p><p class="value">{{ request().total_days }} day(s) · {{ request().is_half_day == 1 ? 'Half day included' : 'Full day request' }}</p></div>
                </div>
              </section>

              <section class="rounded-xl border border-[#E7EEF5] bg-white shadow-sm p-5 space-y-5">
                <div><p class="label">Reason for leave</p><p class="mt-1 text-sm leading-6 text-slate-700 whitespace-pre-line">{{ request().reason }}</p></div>
                @if (request().handover_notes) { <div><p class="label">Handover notes</p><p class="detail">{{ request().handover_notes }}</p></div> }
                @if (request().contact_during_leave) { <div><p class="label">Contact during leave</p><p class="detail">{{ request().contact_during_leave }}</p></div> }
                @if (request().expected_due_or_adoption_date) { <div><p class="label">Expected due or adoption date</p><p class="detail">{{ request().expected_due_or_adoption_date | date:'dd MMM yyyy' }}</p></div> }
                @if (request().proof_note || request().proof_file_url) {
                  <div class="rounded-lg border border-amber-100 bg-amber-50 p-4">
                    <p class="label !text-amber-700">Supporting proof</p>
                    @if (request().proof_note) { <p class="mt-1 text-sm text-amber-900 whitespace-pre-line">{{ request().proof_note }}</p> }
                    @if (request().proof_file_url) { <a [href]="assetUrl(request().proof_file_url)" target="_blank" class="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-900 underline"><i class="ti ti-paperclip"></i> Open proof file</a> }
                  </div>
                }
              </section>
            </main>

            <aside class="space-y-5">
              <section class="rounded-xl border border-[#E7EEF5] bg-white shadow-sm p-5">
                <p class="text-sm font-bold text-slate-900">Review</p>
                @if (request().status === 'Pending' && canApprove()) {
                  <p class="text-xs text-slate-500 mt-1">Approve or decline this request. The employee will be notified by email.</p>
                  <div class="mt-4 grid grid-cols-2 gap-2">
                    <button (click)="openDecision('Approved')" class="h-9 rounded-lg bg-emerald-600 text-white text-xs font-bold">Approve</button>
                    <button (click)="openDecision('Declined')" class="h-9 rounded-lg bg-rose-600 text-white text-xs font-bold">Decline</button>
                  </div>
                } @else if (request().status !== 'Pending') {
                  <div class="mt-4 space-y-3">
                    <div><p class="label">Decision by</p><p class="value">{{ request().decided_by_name || 'Admin' }}</p></div>
                    <div><p class="label">Decision date</p><p class="value">{{ request().decided_at | date:'dd MMM yyyy, h:mm a' }}</p></div>
                    @if (request().admin_notes) { <div><p class="label">Admin note</p><p class="detail">{{ request().admin_notes }}</p></div> }
                  </div>
                } @else {
                  <p class="text-xs text-slate-500 mt-2">Your profile is not enabled to approve leave requests.</p>
                }
              </section>
              <section class="rounded-xl border border-[#E7EEF5] bg-white shadow-sm p-5 space-y-3">
                <div><p class="label">Submitted</p><p class="value">{{ request().created_at | date:'dd MMM yyyy, h:mm a' }}</p></div>
                <div><p class="label">Request ID</p><p class="value">#{{ request().id }}</p></div>
              </section>
            </aside>
          </div>
        } @else {
          <div class="rounded-xl border border-slate-200 bg-white p-16 text-center"><p class="font-semibold text-slate-700">Leave request not found.</p><a routerLink="/admin/operations/leave-requests" class="mt-3 inline-block text-xs font-bold text-[#1A6FA6]">Back to leave requests</a></div>
        }
      </div>
    </div>

    @if (decisionType()) {
      <div class="fixed inset-0 z-[100] bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
          <div class="px-5 py-4 border-b border-slate-100"><p class="text-sm font-bold text-slate-900">{{ decisionType() }} Leave Request</p></div>
          <div class="p-5 space-y-4">
            <label class="text-xs font-semibold text-slate-600">Admin note {{ decisionType() === 'Declined' ? '*' : '' }}<textarea [(ngModel)]="decisionNotes" rows="4" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea></label>
            <label class="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
              <input type="checkbox" [(ngModel)]="attachDecisionPdf" class="mt-0.5 rounded border-slate-300">
              <span><strong class="block text-slate-800">Attach leave PDF</strong>Send the approved or declined leave application PDF to staff and leave-approval admins.</span>
            </label>
          </div>
          <div class="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button (click)="closeDecision()" class="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold">Cancel</button>
            <button (click)="submitDecision()" [disabled]="isSaving()" class="h-9 px-4 rounded-lg text-white text-xs font-bold disabled:opacity-50" [ngClass]="decisionType() === 'Approved' ? 'bg-emerald-600' : 'bg-rose-600'">{{ decisionType() }}</button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #94a3b8; }
    .value { margin-top: 3px; font-size: 13px; font-weight: 600; color: #0f172a; }
    .detail { margin-top: 4px; font-size: 13px; line-height: 1.5; color: #475569; white-space: pre-line; }
  `]
})
export class AdminLeaveRequestDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  request = signal<any>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  canApprove = signal(false);
  decisionType = signal<'Approved' | 'Declined' | null>(null);
  decisionNotes = '';
  attachDecisionPdf = true;

  ngOnInit() {
    this.loadRequest();
    this.api.getProfile().subscribe({ next: (res: any) => this.canApprove.set(Number(res.data?.profile?.can_approve_leave || 0) === 1) });
  }

  loadRequest() {
    const id = this.route.snapshot.paramMap.get('id');
    this.isLoading.set(true);
    this.api.getLeaveRequests({ id }).subscribe({
      next: (res: any) => { this.request.set(res.data?.leave_requests?.[0] || null); this.isLoading.set(false); },
      error: () => { this.request.set(null); this.isLoading.set(false); }
    });
  }

  assetUrl(path: string) { return this.api.assetUrl(path); }
  openDecision(type: 'Approved' | 'Declined') { this.decisionType.set(type); this.decisionNotes = ''; this.attachDecisionPdf = true; }
  closeDecision() { this.decisionType.set(null); this.decisionNotes = ''; this.attachDecisionPdf = true; }

  submitDecision() {
    const type = this.decisionType();
    if (!type || !this.request()) return;
    if (type === 'Declined' && !this.decisionNotes.trim()) return showToast('error', 'Decline reason is required.');
    this.isSaving.set(true);
    this.api.decideLeaveRequest({ id: this.request().id, decision: type, admin_notes: this.decisionNotes, attach_pdf: this.attachDecisionPdf }).subscribe({
      next: () => { this.isSaving.set(false); showToast('success', `Leave request ${type.toLowerCase()}.`); this.closeDecision(); this.loadRequest(); },
      error: (err) => { this.isSaving.set(false); showToast('error', err.error?.message || 'Could not update leave request.'); }
    });
  }

  statusBadgeClass(status: string) {
    if (status === 'Approved') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Declined' || status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-amber-700 bg-amber-50 border-amber-100';
  }
}
